import React from 'react';
import Button from '../../../../components/ui/Button';
import {
  X,
  Users,
  Mail,
  Phone,
  Building2,
  MapPin,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm text-foreground">{value ?? 'N/A'}</p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
};

const formatBoolean = (value) => (value ? 'Yes' : 'No');

const CustomerDetailsModal = ({ isOpen, onClose, customer, isLoading, error }) => {
  if (!isOpen) return null;

  const customerData = customer?.customer_data || {};
  const basicInfo = customerData?.basic_information || customerData;
  const billingAddress = customerData?.billing_address || {};
  const shippingAddress = customerData?.shipping_address || {};
  const bankingDetails = customerData?.banking_details || {};
  const taxDetails = customerData?.tax_details || {};

  const statusBadge = (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        customer?.is_active !== false
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {customer?.is_active !== false ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Customer Details</p>
                <h2 className="text-xl font-semibold text-foreground">
                  {basicInfo?.customer_name || basicInfo?.display_name || 'Customer'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {basicInfo?.entity_type || '—'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Created {formatDateTime(customer?.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Updated {formatDateTime(customer?.updated_at)}</span>
              </div>
              {basicInfo?.customer_id && (
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>Customer ID: {basicInfo.customer_id}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {statusBadge}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading customer details...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Company Overview
                  </h3>
                  <DetailField label="Display Name" value={basicInfo?.display_name} />
                  <DetailField label="Business Name" value={basicInfo?.business_name} />
                  <DetailField label="GSTIN" value={basicInfo?.customer_gstin} />
                  <DetailField label="GST Registered Name" value={basicInfo?.gst_registered_name} />
                  <DetailField label="GST Filing Status" value={basicInfo?.gst_filing_status} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground">Contact Information</h3>
                  <DetailField label="Email" value={basicInfo?.email_address} />
                  <DetailField label="Mobile" value={basicInfo?.mobile_number} />
                  <DetailField label="Phone" value={basicInfo?.phone_number} />
                  <DetailField label="Fax" value={basicInfo?.fax} />
                </div>
              </section>

              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Billing Address
                  </h3>
                  <DetailField label="Address Line 1" value={billingAddress?.address_line_1} />
                  <DetailField label="Address Line 2" value={billingAddress?.address_line_2} />
                  <DetailField label="City" value={billingAddress?.city} />
                  <DetailField label="State" value={billingAddress?.state} />
                  <DetailField label="Postal Code" value={billingAddress?.postal_code} />
                  <DetailField label="Country" value={billingAddress?.country} />
                  <DetailField label="Branch Name" value={billingAddress?.branch_name} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Shipping Address
                  </h3>
                  <DetailField label="Address Line 1" value={shippingAddress?.address_line_1} />
                  <DetailField label="Address Line 2" value={shippingAddress?.address_line_2} />
                  <DetailField label="City" value={shippingAddress?.city} />
                  <DetailField label="State" value={shippingAddress?.state} />
                  <DetailField label="Postal Code" value={shippingAddress?.postal_code} />
                  <DetailField label="Country" value={shippingAddress?.country} />
                  <DetailField label="Branch Name" value={shippingAddress?.branch_name} />
                </div>
              </section>

              <section className="p-4 border border-gray-200 rounded-lg space-y-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Banking Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DetailField label="Account Number" value={bankingDetails?.account_number} />
                  <DetailField label="Account Name" value={bankingDetails?.account_name} />
                  <DetailField label="Bank Name" value={bankingDetails?.bank_name} />
                  <DetailField label="Account Type" value={bankingDetails?.account_type} />
                  <DetailField label="IFSC Code" value={bankingDetails?.ifsc_code} />
                  <DetailField label="Branch Name" value={bankingDetails?.branch_name} />
                </div>
              </section>

              <section className="p-4 border border-gray-200 rounded-lg space-y-4">
                <h3 className="text-sm font-semibold text-foreground">Tax Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DetailField label="PAN" value={taxDetails?.pan} />
                  <DetailField label="TAN" value={taxDetails?.tan} />
                  <DetailField label="COA" value={taxDetails?.coa} />
                  <DetailField label="TDS Slab Rate" value={taxDetails?.tds_slab_rate} />
                  <DetailField label="Currency" value={taxDetails?.currency} />
                  <DetailField label="MSME Number" value={taxDetails?.msme_number} />
                  <DetailField label="Customer Code" value={taxDetails?.customer_code} />
                  <DetailField label="Reverse Charge Default" value={formatBoolean(taxDetails?.apply_reverse_charge_by_default)} />
                  <DetailField label="Export or SEZ Developer" value={formatBoolean(taxDetails?.export_or_sez_developer)} />
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;

